import React, { useState } from 'react';
import { ConfigProvider, Popover, Input } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Upload from '../../atoms/Icon/Upload';
import Share from '../../atoms/Icon/Share';
import Microphone from '../../atoms/Icon/Microphone';
import Tool from '../../atoms/Icon/Tool';
import Search from '../../atoms/Icon/Search';
import Button from '../../atoms/Button/Button';
import ExportMenu, { ExportFormat } from '../ExportMenu/ExportMenu';
import PDF from '../../atoms/Icon/PDF';
import PPT from '../../atoms/Icon/PPT';
import DOCX from '../../atoms/Icon/DOCX';
import Excel from '../../atoms/Icon/Excel';

export type ToolPanelExportFormat = 'pdf' | 'ppt' | 'xlsx' | 'docx';

export interface ToolPanelItem {
    /** Unique identifier for the tool */
    id: string;
    /** Label to display */
    label: string;
    /** Icon type */
    icon: 'export' | 'share' | 'microphone' | 'custom';
    /** Custom icon component (when icon is 'custom') */
    customIcon?: React.ReactNode;
    /** Click handler */
    onClick?: () => void;
    /** Whether the item is disabled */
    disabled?: boolean;
}

export interface ToolPanelPopoverProps {
    /** Whether the popover is open */
    open?: boolean;
    /** Callback when popover open state changes */
    onOpenChange?: (open: boolean) => void;
    /** Tools to display in the panel */
    tools?: ToolPanelItem[];
    /** Callback when search value changes */
    onSearch?: (value: string) => void;
    /** Callback when Export is clicked with selected format (from ExportMenu popup) */
    onExportClick?: (format: ToolPanelExportFormat) => void;
    /** Callback when Share is clicked */
    onShareClick?: () => void;
    /** Placeholder tool callback */
    onPlaceholderClick?: () => void;
    /** Children element that triggers the popover */
    children?: React.ReactNode;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

const defaultTools: ToolPanelItem[] = [
    { id: 'export', label: 'Export', icon: 'export' },
    { id: 'share', label: 'Share', icon: 'share' },
    { id: 'placeholder', label: 'Placeholder', icon: 'microphone' },
];

const EXPORT_FORMATS: { format: ToolPanelExportFormat; label: string; icon: React.ReactNode }[] = [
    { format: 'pdf', label: 'PDF', icon: <PDF size={20} color="rgba(0,0,0,0.88)" /> },
    { format: 'ppt', label: 'PPT', icon: <PPT size={20} color="rgba(0,0,0,0.88)" /> },
    { format: 'xlsx', label: 'Excel', icon: <Excel size={20} /> },
    { format: 'docx', label: 'Word', icon: <DOCX size={20} color="rgba(0,0,0,0.88)" /> },
];

const ToolPanelPopover: React.FC<ToolPanelPopoverProps> = (props) => {
    const {
        open,
        onOpenChange,
        tools = defaultTools,
        onSearch,
        onExportClick,
        onShareClick,
        onPlaceholderClick,
        children,
    } = props;

    const theme = useTheme();
    const [searchValue, setSearchValue] = useState('');
    const [showExportFormatSelection, setShowExportFormatSelection] = useState(false);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        onSearch?.(value);
    };

    const handleExportFormatSelect = (format: ToolPanelExportFormat) => {
        onExportClick?.(format);
        setShowExportFormatSelection(false);
        onOpenChange?.(false);
    };

    const handleToolClick = (tool: ToolPanelItem) => {
        if (tool.disabled) return;

        // Call specific handlers based on tool id
        if (tool.id === 'export') {
            setShowExportFormatSelection(true);
        } else if (tool.id === 'share' && onShareClick) {
            onShareClick();
        } else if (tool.id === 'placeholder' && onPlaceholderClick) {
            onPlaceholderClick();
        }

        // Call the tool's own onClick handler
        tool.onClick?.();
    };

    const renderIcon = (tool: ToolPanelItem) => {
        switch (tool.icon) {
            case 'export':
                return <Upload size={24} strokeColor="#18181b" />;
            case 'share':
                return <Share size={24} strokeColor="#18181b" />;
            case 'microphone':
                return <Microphone size={24} strokeColor="#18181b" />;
            case 'custom':
                return tool.customIcon;
            default:
                return null;
        }
    };

    // Filter tools based on search
    const filteredTools = tools.filter((tool) => tool.label.toLowerCase().includes(searchValue.toLowerCase()));

    const handlePopoverOpenChange = (open: boolean) => {
        if (!open) {
            setShowExportFormatSelection(false);
        }
        onOpenChange?.(open);
    };

    const content = (
        <div
            className="flex flex-col w-[314px] bg-white rounded-xl overflow-hidden"
            style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e4e4e7',
            }}
            data-test={props['data-test']}
            data-test-id={props['data-test-id']}
            data-asper-tool-panel
        >
            {/* Header */}
            <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                <p className="font-sans font-semibold text-[16px] leading-normal text-[#18181b] m-0">
                    {showExportFormatSelection ? 'Export' : 'Tool panel'}
                </p>
                {showExportFormatSelection && (
                    <button
                        type="button"
                        className="text-[14px] text-[#5f49bc] hover:underline cursor-pointer"
                        onClick={() => setShowExportFormatSelection(false)}
                        data-test-id={`${props['data-test-id']}-export-back`}
                    >
                        Back
                    </button>
                )}
            </div>
            <div className="h-px bg-[rgba(0,0,0,0.06)] w-full" />

            {/* Content */}
            <div className="flex flex-col gap-4 px-4 py-4">
                {showExportFormatSelection ? (
                    /* Export format selection with ExportMenu popup for each format */
                    <div className="flex flex-col gap-2">
                        {EXPORT_FORMATS.map(({ format, label, icon }) => (
                            <ExportMenu
                                key={format}
                                format={format as ExportFormat}
                                onExport={(_, opts) => handleExportFormatSelect(format)}
                                onOpenChange={(open) => open === false && setShowExportFormatSelection(false)}
                                data-test-id={`${props['data-test-id']}-export-${format}`}
                            >
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-[rgba(0,0,0,0.08)] hover:border-[#bab1e2] hover:bg-[#fafafa] transition-all text-left cursor-pointer"
                                    data-test-id={`${props['data-test-id']}-export-format-${format}`}
                                >
                                    <div className="bg-[#f4f4f5] rounded-md p-1.5 flex items-center justify-center w-8 h-8">
                                        {icon}
                                    </div>
                                    <span className="font-sans font-normal text-[14px] text-[#18181b]">
                                        Export as {label}
                                    </span>
                                </button>
                            </ExportMenu>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Info Banner */}
                        <div
                            className="w-full rounded-lg px-3 py-2.5"
                            style={{
                                background:
                                    'linear-gradient(94.48deg, rgb(252, 238, 252) 0.66%, rgb(254, 248, 233) 108.4%)',
                            }}
                        >
                            <div className="flex gap-2.5 items-center w-full">
                                <div className="bg-white rounded-md p-1.5 flex items-center justify-center w-8 h-8 border border-[rgba(0,0,0,0.06)] shrink-0">
                                    <Tool size={16} color="#826FD0" />
                                </div>
                                <p className="font-sans font-normal text-[12px] text-[#18181b] m-0 leading-[18px]">
                                    Configure analysis settings & assemble outputs into a case.
                                </p>
                            </div>
                        </div>

                        {/* Search Input */}
                        <div className="w-full flex">
                            <Input
                                placeholder="Search"
                                value={searchValue}
                                onChange={handleSearchChange}
                                className="flex-1 rounded-r-none border-r-0"
                                data-test-id={`${props['data-test-id']}-search-input`}
                            />
                            <Button
                                type="default"
                                className="rounded-l-none border-l-0 w-8 h-8 p-0 flex items-center justify-center"
                                icon={<Search size={16} strokeColor="rgba(0,0,0,0.45)" />}
                            />
                        </div>

                        {/* Tool Cards */}
                        <div className="flex gap-2 items-stretch w-full">
                            {filteredTools.map((tool) => (
                                <button
                                    key={tool.id}
                                    type="button"
                                    disabled={tool.disabled}
                                    className={`
                                        bg-white border border-[rgba(0,0,0,0.08)] rounded-lg 
                                        flex flex-col items-center justify-center 
                                        py-3 px-2 flex-1 min-w-0
                                        ${tool.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#bab1e2] hover:bg-[#fafafa] transition-all'}
                                        focus:outline-none focus:ring-2 focus:ring-[#bab1e2] focus:ring-offset-1
                                    `}
                                    onClick={() => handleToolClick(tool)}
                                    data-test-id={`${props['data-test-id']}-tool-${tool.id}`}
                                >
                                    <div className="flex flex-col gap-1.5 items-center justify-center">
                                        <div className="bg-[#f4f4f5] rounded-md p-1.5 flex items-center justify-center w-8 h-8">
                                            {renderIcon(tool)}
                                        </div>
                                        <p className="font-sans font-normal text-[13px] leading-[18px] text-[#18181b] m-0">
                                            {tool.label}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <ConfigProvider theme={theme}>
            <Popover
                content={content}
                trigger="click"
                open={open}
                onOpenChange={handlePopoverOpenChange}
                placement="bottomRight"
                arrow={false}
                rootClassName="tool-panel-popover"
                overlayInnerStyle={{ padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }}
            >
                {children}
            </Popover>
        </ConfigProvider>
    );
};

export default ToolPanelPopover;
